import { useEffect, useState, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import Navigation from './components/layout/Navigation';
import Footer from './components/layout/Footer';
import LoadingScreen from './components/layout/LoadingScreen';
import ScrollToTop from './components/common/ScrollToTop';
import ScrollToTopButton from './components/common/ScrollToTopButton';
import { Routes, Route, useLocation } from 'react-router-dom';
import { DashboardAuthProvider } from './contexts/DashboardAuthContext';
import { Toaster } from 'react-hot-toast';

// Lazy load pages
const Home = lazy(() => import('./components/pages/Home'));
const ServicesPage = lazy(() => import('./components/pages/ServicesPage'));
const GalleryPage = lazy(() => import('./components/pages/GalleryPage'));
const Testimonials = lazy(() => import('./components/pages/Testimonials'));
const KitchensBathroomsPage = lazy(() => import('./components/pages/services/KitchensBathroomsPage'));
const ExtensionsPage = lazy(() => import('./components/pages/services/ExtensionsPage'));
const ContactPage = lazy(() => import('./components/pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./components/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./components/pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./components/pages/CookiePolicyPage'));
const SitemapPage = lazy(() => import('./components/pages/SitemapPage'));

// Dashboard - lazy loaded
const DashboardWrapper = lazy(() => import('./components/dashboard/DashboardWrapper'));

// Main website layout component
function MainWebsite() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1800);

    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
    >
      {loading ? (
        <LoadingScreen />
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration:.5, ease: 'easeOut' }}
        >
          <ScrollToTop />
          <div id="pageTop" style={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 1000 }}>
            <Navigation />
          </div>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/kitchens-bathrooms" element={<KitchensBathroomsPage />} />
              <Route path="/services/extensions" element={<ExtensionsPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/sitemap" element={<SitemapPage />} />
            </Routes>
          </Suspense>
          <Footer />
          <ScrollToTopButton />
        </motion.div>
      )}
    </motion.div>
  );
}

function App() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/_dashboard');

  // Dashboard routes
  if (isDashboard) {
    return (
      <DashboardAuthProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/_dashboard/*" element={<DashboardWrapper />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </DashboardAuthProvider>
    );
  }

  // Main website routes
  return (
    <>
      <MainWebsite />
      <Toaster position="top-right" />
    </>
  );
}

export default App;
